import axios from 'axios';
import * as t from "./types";
import {
    ExtractJobStatus,
    UnitOfProcessing
} from './backend/extractions.types.generated';
import {
    parseOrganization
} from './type_parse';
import { createBackend } from './backend/combined_backend';
import { BUILD_NUMBER } from './commit';
import { IOrganization, NumberFormatLocale, OrgType } from './backend/auth.types.generated';
import { getTelemetryTraceContext, generateSpanId } from './telemetry';

// AUTH
export interface ISendMagicLinkRequest {
    email_address: string;
}

export interface IRegisterRequest {
    email_address: string;
    password: string;
    first_name: string;
    last_name: string;
}

export interface IPasswordResetRequest {
    email_address: string;
}

export interface IAuthSourceRequest {
    email_address: string;
}

export interface ILoginRequest {
    email_address: string;
    password: string;
}

export interface INewPasswordRequest {
    password: string;
}

export interface IChangePasswordRequest {
    old_password: string;
    new_password: string;
}

export interface ISetUserDebugRequest {
    debug: boolean;
}

export interface IOrgRequest {
    org_uuid: string;
}

export interface IOrgUsersRequest {
    org_uuid: string;
    role?: t.OrgRole;
}

export type EnterpriseSsoProvider = "microsoft" | "okta";

export interface IInsertSsoRequest {
    org_uuid: string;
    email_suffix: string;
    provider: EnterpriseSsoProvider;
    tenant_id: string;
    client_id: string;
    client_secret: string;
    scope_flags: t.IOrgSingleSignOnScope;
}

export interface IGetSsoRequest {
    org_uuid: string;
}

export interface IGetSsoCallbackUrlRequest {
    org_uuid: string;
    provider: EnterpriseSsoProvider;
}

export interface IToggleSsoEnabledRequest {
    org_uuid: string;
    is_enabled: boolean;
}

export interface IUpdateSsoScopeFlagsRequest {
    org_uuid: string;
    scope_flags: t.IOrgSingleSignOnScope;
}

export interface IDeleteSsoRequest {
    org_uuid: string;
}

export interface IInviteUserRequest {
    org_uuid: string;
    email: string;
}

export interface IAddUserToOrgRequest {
    org_uuid: string;
    user_uuid: string;
    role: t.OrgRole;
}

export interface IRemoveUserFromOrgRequest {
    org_uuid: string;
    user_uuid: string;
}

// ADMIN

export interface IUsersRequest {
    offset: number;
    limit: number;
    search?: string;
    role?: string;
    exclude_disabled?: boolean;
}

export interface IUsersByUuidRequest {
    uuids: string[];
}

export interface IUserByUuidRequest {
    uuid: string;
}

export interface ISetUserEmailRequest {
    uuid: string;
    email: string;
}

export interface IUserRoleRequest {
    uuid: string;
    role: string;
}

export interface IUserOnboardingStepRequest {
    uuid: string;
    step: string;
}

export interface IAdminSetUserDebugRequest {
    uuid: string;
    debug: boolean;
}

export interface IUserStatsRequest {
    uuid: string;
}

export interface IAdminUserMultiRegionStatusRequest {
    uuid: string;
}

export interface IAdminSetUserMultiRegionStatusRequest {
    uuid: string;
    multi_region_enabled: boolean;
}

export interface IAdminUserItemsRequest {
    user_uuid: string;
    offset: number;
    limit: number;
}

export interface IAdminUserItemRequest {
    user_uuid: string;
    item_uuid: string;
}

export interface IAdminCreateOrgRequest {
    org_name: string;
    org_type: OrgType;
    admin_user_uuid: string;
}

export interface IAdminAddUserToOrgRequest {
    org_uuid: string;
    user_uuid: string;
    role: t.OrgRole;
}

export interface IAdminRemoveUserFromOrgRequest {
    org_uuid: string;
    user_uuid: string;
}

export interface IAdminOrgRequest {
    org_uuid: string;
}

export interface IAdminSetOrgTagFlagRequest {
    org_uuid: string;
    tag: "sso_enabled" | "eval_factor_credits" | "admin_org" | "csv_use_semicolon" | "max_users" | "number_format_locale" | "cannot_have_negative_balance";
    flag: boolean | number | NumberFormatLocale | undefined;
}

export type BackgroundJobName = "close_timeout_extract_jobs" | "forward_email_digests" | "validate_db_context_fields" |
    "validate_db_context_extract_params" | "validate_db_scrape_records" | "validate_db_endpoint_details" | "validate_db_endpoint_actions" |
    "validate_db_template_field_uuids" | "validate_db_template_context_ocr_strategies" | "sync_data_warehouse" |
    "handle_user_notifications" | "expire_credits" | "fix_item_documents_preprocess_params" | "sync_user_region_mappings" |
    "sync_user_details_with_crm" | "validate_db_context_overrides" | "validate_kb_tables" | "validate_db_widgets" | "classify_templates";

export interface IRunBackgroundJobsRequest {
    job_name: BackgroundJobName;
}

export interface IDemoUsageRequest {
    offset: number;
    limit: number;
}

export interface IPromptLogRequest {
    offset: number;
    limit: number;
    job_uuid?: string;
}

export interface IConstAggrRequest {
}

// ONBOARDING
export interface IOnboardingUpdateUserDetailsRequest {
    details: t.IUserDetails;
}

export interface IOnboardingUpdateStepRequest {
    step: string;
}

// TEMPLATE SUGGEST

export interface ITemplateSuggestStartRequest {
    guidelines?: string;
    input_text?: string;
    input_file?: File;
}

export interface ITemplateSuggestGetRequest {
    job_uuid: string;
}

// DOC BOUNDARY DETECTION

export interface IDocBoundaryDetectionStartRequest {
    guidelines?: string;
    input_text?: string;
    input_file?: File;
}

export interface IDocBoundaryDetectionGetRequest {
    job_uuid: string;
}

// SCRAPES

export interface IScrapeCheckStartRequest {
    template_uuid?: string;
    endpoint_uuid?: string;
    scrape_name: string;
    input_text?: string;
    input_files?: File[];
    input_files_selected_pages?: number[][];
    input_files_page_delimiters?: number[][];
    input_item_uuid?: string;
    // options
    store_extractions: boolean;
    unit_of_processing: UnitOfProcessing;
}

export interface IScrapeCheckGetRequest {
    job_uuid: string;
}

// ITEMS

export interface IItemDeleteRequest {
    item_uuid: string;
}

// ATTACHMENTS

export interface IExcelAttachmentRequest {
    uuid: string;
}

export interface IAttachmentRequest {
    uuid: string;
    filename: string;
    mimetype: string;
}

// DOWNLOAD

export interface IDownloadContextRequest {
    context_uuid: string;
}

// DEMO

export interface IDemoCheckRequest {
    fields: t.IContextField[];
    example_text: string;
}

export interface IConnectorLeadRequest {
    email_address: string;
}

// PROCESSING JOBS

export interface IExtractJobsRequest {
    offset: number;
    limit: number;
    endpoint_uuid?: string;
    status?: ExtractJobStatus;
}

export interface IOrgExtractJobsRequest {
    org_uuid: string;
    offset: number;
    limit: number;
}

export interface IAdminStatusExtractJobsRequest {
    statuses: ExtractJobStatus[];
    offset: number;
    limit: number;
    search?: string;
}

export interface IExtractJobRequest {
    job_uuid: string;
}

export interface IOrgExtractJobRequest {
    org_uuid: string;
    job_uuid: string;
}

export interface IExtractJobStatusRequest {
    job_uuid: string;
}

export interface IAdminExtractJobsRequest {
    user_uuid?: string;
    org_uuid?: string;
    offset: number;
    limit: number;
}

export interface IAdminExtractJobRequest {
    user_uuid: string;
    job_uuid: string;
}

// AUDIT

export interface IUserAuditLogRequest {
    offset: number;
    limit: number;
}

export interface IOrgAuditLogRequest {
    org_uuid: string;
    offset: number;
    limit: number;
}

export interface IAdminUserAuditLogRequest {
    user_uuid: string;
    offset: number;
    limit: number;
}

export interface IAdminOrgAuditLogRequest {
    org_uuid: string;
    offset: number;
    limit: number;
}

export interface IAdminLogContextDefDownloadRequest {
    context_uuid: string;
}

// REGIONS

export interface ICheckRegionRedirectRequest {
    email_address_hash: string;
}

export interface ICheckRegionRedirectResult {
    redirect: boolean;
    domain?: string;
}

export interface IRegionsResult {
    regions?: t.IRegionInfo[];
    current_region?: t.IRegionInfo;
}

// AUXILIARY

export interface ISourceIpsResult {
    ips: string[];
}

////////////////////////////////////////////////////////////////////////////////////////////////////
// Initialize axios instance for calling the server

const api = axios.create({});
api.defaults.headers.common['x-client-commit-id'] = BUILD_NUMBER;

// add telemetry correlation headers
api.interceptors.request.use(
    (config) => {
        const operation_context = getTelemetryTraceContext();
        if (operation_context?.traceID) {
            config.headers = config.headers || {};
            config.headers["request-id"] = operation_context.traceID;
            const span_id = generateSpanId();
            config.headers["traceparent"] = `00-${operation_context.traceID}-${span_id}-01`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        if (response.status === 302) {
            window.location.href = response.headers?.location || '/';
        }
        return response;
    },
    (error) => {
        const call_url: string = error.request.responseUrl ?? error.request.responseURL;
        // when we are calling auth/login and get 401 then we entered wrong credentials
        // this is handled within /login screen so no need to redirect
        if (error.response && error.response.status === 401 && !call_url.endsWith("/auth/login")) {
            window.location.href = "/login";
        }
        // if we receive status 400, it means that we have validation error
        // compare the commit id from client and server
        if (error.response && error.response.status === 400) {
            const commit_id = error.response.headers["x-server-commit-id"] ?? "";
            const server_env = error.response.headers["x-server-env"] ?? "";
            if (commit_id !== BUILD_NUMBER && commit_id !== "") {
                // here we definitely know that the versions don't match
                // we need to hard-reload the page to get the new version
                if (server_env === "dev") {
                    console.error(`Client commit id: ${BUILD_NUMBER}, Server commit id: ${commit_id}`);
                    console.error("Server is in dev mode, skipping reload");
                } else {
                    window.location.reload();
                }
            }
        }
        // check if we have error message in response
        if (error.response && error.response.data && error.response.data.message) {
            return Promise.reject(new Error(error.response.data.message));
        }
        // no error message, return generic error
        return Promise.reject(error);
    }
)

////////////////////////////////////////////////////////////////////////////////////////////////////
// Prepare wrappers for backend endpoints

export const BackendObj = createBackend(api);

export class Backend {
    static async getHealth(): Promise<t.IHealth> {
        const { data } = await api.get(`/api/health`);
        return {
            timestamp: data.timestamp,
            now_date: data.now_date,
            last_heartbeat: data.last_heartbeat,
            build_date: data.build_date,
            uptime_seconds: data.uptime_seconds,
            start_date: data.start_date,
            commit_hash: data.commit_hash,
            db_version: data.db_version,
            cache_connected: data.cache_connected,
            db_connected: data.db_connected,
            status: data.status
        }
    }

    // AUTH

    static async sendMagicLink(req: ISendMagicLinkRequest): Promise<"ok" | "unknown_email" | "sso_email"> {
        const { data } = await api.post(`/auth/magic_link`, req);
        return data.status;
    }

    static async register(req: IRegisterRequest): Promise<{ status: "ok" | "sso" | "error", org_contacts?: { name: string, email: string }[] }> {
        const { data } = await api.post(`/auth/register`, req);
        return {
            status: data.status,
            org_contacts: data.org_contacts
        };
    }

    static async passwordReset(req: IPasswordResetRequest): Promise<void> {
        await api.post(`/auth/reset`, req);
    }

    static async authSource(req: IAuthSourceRequest): Promise<string> {
        const { data } = await api.post(`/auth/source`, req);
        return data.auth_source;
    }

    static async login(req: ILoginRequest): Promise<void> {
        await api.post(`/auth/login`, req);
    }

    static async newPassword(req: INewPasswordRequest): Promise<boolean> {
        const { data } = await api.post(`/auth/new_password`, req);
        return data.ok
    }

    static async changePassword(req: IChangePasswordRequest): Promise<boolean> {
        const { data } = await api.post(`/auth/change_password`, req);
        return data.ok
    }

    static async getUser(): Promise<{
        user?: t.IUser,
        memberships?: t.IMembership[],
        is_sso?: boolean,
        onboarding_status?: t.IOnboardingStatus,
        env: string,
        self_url: string,
        region_info?: t.IRegionInfo
    }> {
        const { data } = await api.get(`/auth/user`);
        const user: t.IUser | undefined = data.user ? {
            uuid: data.user.uuid,
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            email: data.user.email,
            reset_password: data.user.reset_password,
            auth_source: data.user.auth_source,
            is_enabled: data.user.is_enabled,
            picture_url: data.user.picture_url,
            role: data.user.role,
            details: data.user.details,
            debug: data.user.debug,
            created_at: data.user.created_at,
            notify_about_changes: data.user.notify_about_changes,
            number_format_locale: data.user.number_format_locale
        } : undefined;
        const memberships: t.IMembership[] | undefined = data.memberships ?
            data.memberships.map((m: any): t.IMembership => ({
                org: parseOrganization(m.org),
                role: m.role,
            })) : undefined;
        const is_sso: boolean | undefined = data.is_sso;
        const env: string = data.env;
        const self_url: string = data.self_url;
        const onboarding_status: t.IOnboardingStatus | undefined = data.onboarding_status ? {
            step: data.onboarding_status.step,
            start_ts: data.onboarding_status.start_ts,
            end_ts: data.onboarding_status.end_ts
        } : undefined;
        const region_info: t.IRegionInfo | undefined = data.region_info;
        return { user, memberships, is_sso, onboarding_status, env, self_url, region_info };
    }

    static async logout(): Promise<void> {
        await api.get(`/auth/logout`);
    }

    static async getUserBalances(): Promise<t.IOrgBalance[]> {
        const { data } = await api.get(`/api/user_balances`);
        return data.balances.map((b: any): t.IOrgBalance => ({
            org: parseOrganization(b.org),
            balance: b.balance
        }));
    }

    static async getUserSubscriptions(): Promise<t.IOrgSubscription[]> {
        const { data } = await api.get(`/api/user_subscriptions`);
        return data.subscriptions.map((b: any): t.IOrgSubscription => ({
            org: parseOrganization(b.org),
            subscription: b.subscription
        }));
    }

    static async getOrg(req: IOrgRequest): Promise<{ org: IOrganization, users: t.IOrgUser[], balance: number, region_info: t.IRegionInfo }> {
        const { data } = await api.get(`/api/org?org_uuid=${req.org_uuid}`);
        return {
            org: parseOrganization(data.org),
            users: data.users.map((u: any): t.IOrgUser => ({
                uuid: u.uuid,
                first_name: u.first_name,
                last_name: u.last_name,
                email: u.email,
                picture_url: u.picture_url,
                user_role: u.user_role,
                created_at: u.created_at,
                org_role: u.org_role
            })),
            balance: data.balance,
            region_info: data.region_info
        };
    }

    static async setUserDebug(req: ISetUserDebugRequest): Promise<void> {
        await api.post("/api/user_debug", { debug: req.debug ? "true" : "false" });
    }

    static async getOrgUsers(req: IOrgUsersRequest): Promise<t.IOrgUser[]> {
        const { data } = await api.get(`/api/org_users?org_uuid=${req.org_uuid}${req.role !== undefined ? `&role=${req.role}` : ""}`);
        return data.users.map((u: any): t.IOrgUser => ({
            uuid: u.uuid,
            first_name: u.first_name,
            last_name: u.last_name,
            email: u.email,
            picture_url: u.picture_url,
            user_role: u.user_role,
            created_at: u.created_at,
            org_role: u.org_role
        }));
    }

    static async insertOrgSingleSignOn(req: IInsertSsoRequest): Promise<void> {
        await api.post(`/api/insert_sso`, req);
    }

    static async getOrgSingleSignOn(req: IGetSsoRequest): Promise<{ sso: t.IOrgSingleSignOn, callback_url: string }> {
        const { data } = await api.get(`/api/get_sso?org_uuid=${req.org_uuid}`);
        return { sso: data.sso, callback_url: data.callback_url };
    }

    static async getOrgSingleSignOnCallbackUrl(req: IGetSsoCallbackUrlRequest): Promise<string> {
        const { data } = await api.get(`/api/get_sso_callback_url?org_uuid=${req.org_uuid}&provider=${req.provider}`);
        return data.callback_url;
    }

    static async toggleOrgSingleSignOnEnabled(req: IToggleSsoEnabledRequest): Promise<void> {
        await api.post(`/api/toggle_sso_enabled`, req);
    }

    static async updateOrgSingleSignOnScopeFlags(req: IUpdateSsoScopeFlagsRequest): Promise<void> {
        await api.post(`/api/sso_scope_flags`, req);
    }

    static async deleteOrgSingleSignOn(req: IDeleteSsoRequest): Promise<void> {
        await api.post(`/api/delete_sso`, req);
    }

    static async inviteUser(req: IInviteUserRequest): Promise<{ status: "added" | "sso" | "limit_reached" | "error", message?: string }> {
        const { data } = await api.post(`/api/invite_user`, req);
        return {
            status: data.status,
            message: data.message
        };
    }

    static async addUserToOrg(req: IAddUserToOrgRequest): Promise<void> {
        await api.post(`/api/add_user_to_org`, req);
    }

    static async removeUserFromOrg(req: IRemoveUserFromOrgRequest): Promise<void> {
        await api.post(`/api/remove_user_from_org`, req);
    }

    // ADMIN

    static async getAdminUserList(req: IUsersRequest): Promise<t.IUserList> {
        let url = `/api/admin/users?offset=${req.offset}&limit=${req.limit}`;
        if (req.search) { url += `&search=${req.search}`; }
        if (req.role) { url += `&role=${req.role}`; }
        if (req.exclude_disabled) { url += `&exclude_disabled=yes`; }
        const { data } = await api.get(url);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            users: data.users.map((u: any): t.IUserWithStatus => ({
                uuid: u.uuid,
                first_name: u.first_name,
                last_name: u.last_name,
                email: u.email,
                auth_source: u.auth_source,
                picture_url: u.picture_url,
                role: u.role,
                reset_password: u.reset_password,
                details: u.details,
                debug: u.debug,
                is_enabled: u.is_enabled,
                created_at: u.created_at,
                onboarding_status: u.onboarding_status,
                org_balances: u.org_balances.map((b: any): t.IOrgBalance => ({
                    org: parseOrganization(b.org),
                    balance: b.balance
                })),
                notify_about_changes: u.notify_about_changes,
                number_format_locale: u.number_format_locale
            }))
        };
    }

    static async getAdminUsersByUuid(req: IUsersByUuidRequest): Promise<t.IUser[]> {
        const { data } = await api.post(`/api/admin/users_by_uuid`, req);
        return data.users.map((u: any): t.IUser => ({
            uuid: u.uuid,
            first_name: u.first_name,
            last_name: u.last_name,
            email: u.email,
            auth_source: u.auth_source,
            is_enabled: u.is_enabled,
            picture_url: u.picture_url,
            role: u.role,
            reset_password: u.reset_password,
            details: u.details,
            debug: u.debug,
            created_at: u.created_at,
            notify_about_changes: u.notify_about_changes,
            number_format_locale: u.number_format_locale
        }));
    }

    static async getAdminUserByUuid(req: IUserByUuidRequest): Promise<{
        user?: t.IUser,
        memberships?: t.IMembership[],
        balances?: t.IOrgBalance[];
        onboarding_status?: t.IOnboardingStatus
        onboarding_events?: t.IOnboardingEvent[]
    }> {
        const { data } = await api.get(`/api/admin/user?uuid=${req.uuid}`);
        const user: t.IUser | undefined = data.user ? {
            uuid: data.user.uuid,
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            email: data.user.email,
            reset_password: data.user.reset_password,
            auth_source: data.user.auth_source,
            is_enabled: data.user.is_enabled,
            picture_url: data.user.picture_url,
            role: data.user.role,
            details: data.user.details,
            debug: data.user.debug,
            created_at: data.user.created_at,
            notify_about_changes: data.user.notify_about_changes,
            number_format_locale: data.user.number_format_locale
        } : undefined;
        const memberships: t.IMembership[] | undefined = data.memberships ?
            data.memberships.map((m: any): t.IMembership => ({
                org: parseOrganization(m.org),
                role: m.role,
            })) : undefined;
        const balances: t.IOrgBalance[] | undefined = data.balances ?
            data.balances.map((b: any): t.IOrgBalance => ({
                org: parseOrganization(b.org),
                balance: b.balance
            })) : undefined;
        const onboarding_status: t.IOnboardingStatus | undefined = data.onboarding_status ? {
            step: data.onboarding_status.step,
            start_ts: data.onboarding_status.start_ts,
            end_ts: data.onboarding_status.end
        } : undefined;
        const onboarding_events: t.IOnboardingEvent[] | undefined = data.onboarding_events ?
            data.onboarding_events.map((e: any): t.IOnboardingEvent => ({
                event: e.event,
                ts: e.ts
            })) : undefined;
        return { user, memberships, balances, onboarding_status, onboarding_events };
    }

    static async setAdminUserEmail(req: ISetUserEmailRequest): Promise<void> {
        await api.post(`/api/admin/user_email`, req);
    }

    static async setAdminUserRole(req: IUserRoleRequest): Promise<void> {
        await api.post(`/api/admin/user_role`, req);
    }

    static async setAdminUserOnboardingStep(req: IUserOnboardingStepRequest): Promise<void> {
        await api.post(`/api/admin/user_onboarding_step`, req);
    }

    static async setAdminUserDebug(req: IAdminSetUserDebugRequest): Promise<void> {
        await api.get(`/api/admin/user_debug?uuid=${req.uuid}&debug=${req.debug ? "true" : "false"}`);
    }

    static async getAdminUserStats(req: IUserStatsRequest): Promise<t.IUserStats> {
        const { data } = await api.get(`/api/admin/user_stats?uuid=${req.uuid}`);
        const stats = data.stats;
        return {
            scrape_count: stats.scrape_count,
            item_count: stats.item_count,
            template_count: stats.template_count,
            endpoint_count: stats.endpoint_count,
            extract_jobs_7_days: stats.extract_jobs_7_days.map((e: any): t.IUserExtractJobStat => ({
                extract_job_type: e.extract_job_type,
                count: e.count
            })),
            extract_jobs_30_days: stats.extract_jobs_30_days.map((e: any): t.IUserExtractJobStat => ({
                extract_job_type: e.extract_job_type,
                count: e.count
            }))
        };
    }

    static async getAdminUserMultiRegionStatus(req: IAdminUserMultiRegionStatusRequest): Promise<t.IUserMultiRegionStatus> {
        const { data } = await api.get(`/api/admin/user_multi_region_status?uuid=${req.uuid}`);
        return {
            multi_region_enabled: data.multi_region_enabled
        };
    }

    static async setAdminUserMultiRegionStatus(req: IAdminSetUserMultiRegionStatusRequest): Promise<void> {
        await api.post(`/api/admin/user_multi_region_status`, req);
    }

    static async getAdminUserItems(req: IAdminUserItemsRequest): Promise<t.IItemList> {
        const { data } = await api.get(`/api/admin/user_items?user_uuid=${req.user_uuid}&offset=${req.offset}&limit=${req.limit}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            items: data.items
        };
    }

    static async getAdminUserItem(req: IAdminUserItemRequest): Promise<t.IItem | undefined> {
        const { data } = await api.get(`/api/admin/user_item?user_uuid=${req.user_uuid}&item_uuid=${req.item_uuid}`);
        return data.item;
    }

    static async createAdminOrg(req: IAdminCreateOrgRequest): Promise<IOrganization> {
        const { data } = await api.get(`/api/admin/create_org?org_name=${req.org_name}&org_type=${req.org_type}&admin_user_uuid=${req.admin_user_uuid}`);
        return parseOrganization(data.org);
    }

    static async adminAddUserToOrg(req: IAdminAddUserToOrgRequest): Promise<void> {
        await api.get(`/api/admin/add_user_to_org?org_uuid=${req.org_uuid}&user_uuid=${req.user_uuid}&role=${req.role}`);
    }

    static async adminRemoveUserFromOrg(req: IAdminRemoveUserFromOrgRequest): Promise<void> {
        await api.get(`/api/admin/remove_user_from_org?org_uuid=${req.org_uuid}&user_uuid=${req.user_uuid}`);
    }

    static async getAdminOrg(req: IAdminOrgRequest): Promise<{ org: IOrganization, users: t.IOrgUser[], balance: number, sso?: t.IOrgSingleSignOn }> {
        const { data } = await api.get(`/api/admin/org?org_uuid=${req.org_uuid}`);
        return {
            org: parseOrganization(data.org),
            users: data.users.map((u: any): t.IOrgUser => ({
                uuid: u.uuid,
                first_name: u.first_name,
                last_name: u.last_name,
                email: u.email,
                picture_url: u.picture_url,
                user_role: u.user_role,
                created_at: u.created_at,
                org_role: u.org_role
            })),
            balance: data.balance,
            sso: data.sso ? {
                org_uuid: data.sso.org_uuid,
                email_suffix: data.sso.email_suffix,
                provider: data.sso.provider,
                tenant_id: data.sso.tenant_id,
                client_id: data.sso.client_id,
                client_secret: data.sso.client_secret,
                scope_flags: data.sso.scope_flags,
                created_at: data.sso.created_at,
                is_enabled: data.sso.is_enabled
            } : undefined
        };
    }

    static async setAdminOrganizationTagFlag(req: IAdminSetOrgTagFlagRequest): Promise<void> {
        await api.post(`/api/admin/set_org_tag_flag`, req);
    }

    static async runAdminBackgroundJobs(req: IRunBackgroundJobsRequest): Promise<void> {
        await api.post(`/api/admin/run_background_jobs`, req);
    }

    static async getAdminDemoUsage(req: IDemoUsageRequest): Promise<t.IDebugDemoUsageList> {
        const { data } = await api.get(`/api/admin/demo_usage?offset=${req.offset}&limit=${req.limit}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            usages: data.usages.map((u: any): t.IDebugDemoUsage => ({
                fields: u.fields,
                example_text: u.example_text,
                example_records: u.example_records,
                visitor_details: u.visitor_details,
                user_uuid: u.user_uuid,
                created_at: u.created_at
            }))
        };
    }

    static async getAdminPromptLogs(req: IPromptLogRequest): Promise<t.IDebugPromptLogList> {
        const { data } = await api.get(`/api/admin/prompt_log?offset=${req.offset}&limit=${req.limit}${req.job_uuid !== undefined ? `&job_uuid=${req.job_uuid}` : ""}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            logs: data.logs.map((l: any): t.IDebugPromptLog => ({
                user_uuid: l.user_uuid,
                user_email: l.user_email,
                model: l.model,
                num_input_tokens: l.num_input_tokens,
                num_output_tokens: l.num_output_tokens,
                duration_ms: l.duration_ms,
                task_type: l.task_type,
                input: l.input,
                output: l.output,
                title: l.title,
                input_name: l.input_name,
                created_at: l.created_at
            }))
        };
    }

    // ONBOARDING

    static async updateUserDetails(req: IOnboardingUpdateUserDetailsRequest): Promise<void> {
        await api.post(`/api/onboarding/update_user_details`, req);
    }

    static async updateOnboardingStep(req: IOnboardingUpdateStepRequest): Promise<t.IOnboardingStatus> {
        const { data } = await api.get(`/api/onboarding/update_step?step=${req.step}`);
        return {
            step: data.step,
            start_ts: data.start_ts,
            end_ts: data.end_ts
        };
    }

    // CONTEXT SUGGEST

    static async suggestTemplateStart(req: ITemplateSuggestStartRequest): Promise<string> {
        // we might have a file so we send it over as a form data
        const { input_text, input_file, guidelines } = req;
        const form_data = new FormData();
        if (input_text) { form_data.append("input_text", input_text); }
        if (input_file) { form_data.append("input_file", input_file); }
        if (guidelines) { form_data.append("guidelines", guidelines); }
        const { data } = await api.post(`/api/template/suggest_start`, form_data, { headers: { "Content-Type": "multipart/form-data" } });
        return data.job_uuid;
    }

    static async suggestTemplateGet(req: ITemplateSuggestGetRequest): Promise<t.ISuggestTemplateResult> {
        const { data } = await api.post(`/api/template/suggest_get`, req);
        return {
            status: data.status,
            template: data.template,
            extraction_time_ts: data.extraction_time_ts,
            message: data.message
        };
    }

    // DOC BOUNDARY DETECTION

    static async docBoundaryDetectionStart(req: IDocBoundaryDetectionStartRequest): Promise<string> {
        const { input_text, input_file, guidelines } = req;
        const form_data = new FormData();
        if (input_text) { form_data.append("input_text", input_text); }
        if (input_file) { form_data.append("input_file", input_file); }
        if (guidelines) { form_data.append("guidelines", guidelines); }
        const { data } = await api.post(`/api/doc/boundary_detection_start`, form_data, { headers: { "Content-Type": "multipart/form-data" } });
        return data.job_uuid;
    }

    static async docBoundaryDetectionGet(req: IDocBoundaryDetectionGetRequest): Promise<t.IDocBoundaryDetectionResult> {
        const { data } = await api.post(`/api/doc/boundary_detection_get`, req);
        return {
            status: data.status,
            sections_pages: data.sections_pages,
            extraction_time_ts: data.extraction_time_ts,
            message: data.message
        }
    }

    // SCRAPES

    static async extractScrapeStart(req: IScrapeCheckStartRequest): Promise<string> {
        // we might have a file so we send it over as a form data
        const {
            template_uuid, endpoint_uuid, scrape_name, input_text, input_files, input_files_selected_pages,
            input_files_page_delimiters, input_item_uuid, store_extractions, unit_of_processing
        } = req;
        const form_data = new FormData();
        form_data.append("scrape_name", scrape_name);
        if (template_uuid) { form_data.append("template_uuid", template_uuid); }
        if (endpoint_uuid) { form_data.append("endpoint_uuid", endpoint_uuid); }
        if (input_text) { form_data.append("input_text", input_text); }
        if (input_files) {
            for (const input_file of input_files) {
                form_data.append("input_file", input_file);
            }
        }
        if (input_files_selected_pages) {
            form_data.append("input_files_selected_pages", JSON.stringify(input_files_selected_pages));
        }
        if (input_files_page_delimiters) {
            form_data.append("input_files_page_delimiters", JSON.stringify(input_files_page_delimiters));
        }
        if (input_item_uuid !== undefined) {
            form_data.append("input_item_uuid", input_item_uuid);
        }
        form_data.append("store_extractions", store_extractions ? "true" : "false");
        form_data.append("unit_of_processing", unit_of_processing);
        const { data } = await api.post(`/api/scrape/check_start`, form_data, { headers: { "Content-Type": "multipart/form-data" } });
        return data.job_uuid;
    }

    static async extractScrapeGet(req: IScrapeCheckGetRequest): Promise<t.IScrapeCheck> {
        const { data } = await api.post(`/api/scrape/check_get`, req);

        return {
            status: data.status,
            template: data.template,
            endpoint: data.endpoint,
            items: data.items,
            confirmations: data.confirmations,
            extraction_time_ts: data.extraction_time_ts,
            message: data.message
        };
    }

    // ITEMS

    static async deleteItem(req: IItemDeleteRequest): Promise<void> {
        await api.get(`/api/item/delete?item_uuid=${req.item_uuid}`);
    }

    // ATTACHMENTS

    static async getExcelAttachment(req: IExcelAttachmentRequest): Promise<t.IExcelArraySheet[]> {
        const { data } = await api.get(`/api/attachment/excel?uuid=${req.uuid}`);
        return data.sheets;
    }

    static async getAttachment(req: IAttachmentRequest): Promise<File> {
        const response = await api.get(`/api/attachment/get?uuid=${req.uuid}`, { responseType: 'blob' });
        const file = new File([response.data], req.filename, { type: req.mimetype });
        return file;
    }

    // DEMO

    static async checkDemo(req: IDemoCheckRequest): Promise<t.IContextDemoCheck> {
        const { data } = await api.post(`/api/demo/check`, req);
        return {
            records: data.example_records
        };
    }

    static async connectorLead(req: IConnectorLeadRequest): Promise<void> {
        await api.get(`/api/connector/lead?email_address=${req.email_address}`);
    }

    // PROCESSING JOBS

    static async getExtractJobs(req: IExtractJobsRequest): Promise<t.IExtractJobList> {
        const { data } = req.endpoint_uuid ?
            await api.get(`/api/extract_jobs?endpoint_uuid=${req.endpoint_uuid}&offset=${req.offset}&limit=${req.limit}`) :
            req.status ?
                await api.get(`/api/extract_jobs?status=${req.status}&offset=${req.offset}&limit=${req.limit}`) :
                await api.get(`/api/extract_jobs?offset=${req.offset}&limit=${req.limit}`);
        return this.parseExtractJobs(data);
    }

    static async getOrgExtractJobs(req: IOrgExtractJobsRequest): Promise<t.IExtractJobList> {
        const { data } = await api.get(`/api/org_extract_jobs?org_uuid=${req.org_uuid}&offset=${req.offset}&limit=${req.limit}`);
        return this.parseExtractJobs(data);
    }

    static async getExtractJob(req: IExtractJobRequest): Promise<t.IExtractJobWithEvents> {
        const { data } = await api.get(`/api/extract_job?job_uuid=${req.job_uuid}`);
        return this.parseExtractJob(data);
    }

    static async getOrgExtractJob(req: IOrgExtractJobRequest): Promise<t.IExtractJobWithEvents> {
        const { data } = await api.get(`/api/org_extract_job?org_uuid=${req.org_uuid}&job_uuid=${req.job_uuid}`);
        return this.parseExtractJob(data);
    }

    static async getExtractJobStatus(req: IExtractJobStatusRequest): Promise<string> {
        const { data } = await api.get(`/api/extract_job_status?job_uuid=${req.job_uuid}`);
        return data.status;
    }

    static async getAdminExtractJobs(req: IAdminExtractJobsRequest): Promise<t.IExtractJobList> {
        const { data } = req.user_uuid ?
            await api.get(`/api/admin/extract_jobs?user_uuid=${req.user_uuid}&offset=${req.offset}&limit=${req.limit}`) :
            await api.get(`/api/admin/extract_jobs?org_uuid=${req.org_uuid}&offset=${req.offset}&limit=${req.limit}`);
        return this.parseExtractJobs(data);
    }

    static async getAdminStatusExtractJobs(req: IAdminStatusExtractJobsRequest): Promise<t.IExtractJobList> {
        const { data } = await api.post(`/api/admin/status_extract_jobs`, req);
        return this.parseExtractJobs(data);
    }

    static async getAdminExtractJob(req: IAdminExtractJobRequest): Promise<t.IExtractJobWithEvents> {
        const { data } = await api.get(`/api/admin/extract_job?user_uuid=${req.user_uuid}&job_uuid=${req.job_uuid}`);
        return this.parseExtractJob(data);
    }

    // AUDIT

    static async getUserAuditLog(req: IUserAuditLogRequest): Promise<t.IUserAuditList> {
        const { data } = await api.get(`/api/user_audit_log?offset=${req.offset}&limit=${req.limit}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            audits: data.audits.map((a: any): t.IUserAudit => ({
                user_uuid: a.user_uuid,
                type: a.type,
                details: a.details,
                created_at: a.created_at
            }))
        };
    }

    static async getOrgAuditLog(req: IOrgAuditLogRequest): Promise<t.IOrgAuditList> {
        const { data } = await api.get(`/api/org_audit_log?org_uuid=${req.org_uuid}&offset=${req.offset}&limit=${req.limit}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            audits: data.audits.map((a: any): t.IOrgAudit => ({
                org_uuid: a.org_uuid,
                type: a.type,
                details: a.details,
                created_at: a.created_at
            }))
        };
    }


    static async getAdminUserAuditLog(req: IAdminUserAuditLogRequest): Promise<t.IUserAuditList> {
        const { data } = await api.get(`/api/admin/user_audit_log?user_uuid=${req.user_uuid}&offset=${req.offset}&limit=${req.limit}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            audits: data.audits.map((a: any): t.IUserAudit => ({
                user_uuid: a.user_uuid,
                type: a.type,
                details: a.details,
                created_at: a.created_at
            }))
        };
    }

    static async getAdminOrgAuditLog(req: IAdminOrgAuditLogRequest): Promise<t.IOrgAuditList> {
        const { data } = await api.get(`/api/admin/org_audit_log?org_uuid=${req.org_uuid}&offset=${req.offset}&limit=${req.limit}`);
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            audits: data.audits.map((a: any): t.IOrgAudit => ({
                org_uuid: a.org_uuid,
                type: a.type,
                details: a.details,
                created_at: a.created_at
            }))
        };
    }

    static async logAdminContextDefDownload(req: IAdminLogContextDefDownloadRequest): Promise<void> {
        await api.get(`/api/admin/log_context_def_download?context_uuid=${req.context_uuid}`);
    }

    static parseExtractJobs(data: any): t.IExtractJobList {
        return {
            offset: data.offset,
            limit: data.limit,
            total: data.total,
            jobs: data.jobs.map((j: any): t.IExtractJobSimple => ({
                uuid: j.uuid,
                user_uuid: j.user_uuid,
                org_uuid: j.org_uuid,
                title: j.title,
                subtitle: j.subtitle,
                type: j.type,
                status: j.status,
                start_ts: j.start_ts,
                end_ts: j.end_ts,
                error: j.error === true,
                endpoint_uuid: j.endpoint_uuid,
                message: j.message
            }))
        };
    }

    static parseExtractJob(data: any): t.IExtractJobWithEvents {
        return {
            job: {
                uuid: data.job.uuid,
                user_uuid: data.job.user_uuid,
                org_uuid: data.job.org_uuid,
                title: data.job.title,
                subtitle: data.job.subtitle,
                type: data.job.type,
                status: data.job.status,
                start_ts: data.job.start_ts,
                end_ts: data.job.end_ts,
                error: data.job.error === true,
                endpoint_uuid: data.job.endpoint_uuid,
                message: data.job.message,
                credits_used: data.job.credits_used,
                exec_costs: data.job.exec_costs,
                attachment_uuids: data.job.attachment_uuids
            },
            events: data.events.map((e: any): t.IExtractJobEventSimple => ({
                uuid: e.uuid,
                job_uuid: e.job_uuid,
                type: e.type,
                status: e.status,
                ts: e.ts,
                message: e.message
            }))
        };
    }

    // DEBUG ATTACHMENTS

    static async getAdminDebugAttachments(job_uuid: string): Promise<t.IDebugAttachment[]> {
        const { data } = await api.get(`/api/admin/debug_attachments?job_uuid=${job_uuid}`);
        return data.debug_attachments.map((attachment: any): t.IDebugAttachment => ({
            filename: attachment.filename,
            blob_name: attachment.blob_name,
            size: attachment.size,
            last_modified: attachment.last_modified,
            metadata: attachment.metadata
        }));
    }

    // CLEAN TEXT

    static async getArrayFromExcel(form_data: FormData): Promise<t.IExcelArraySheet[]> {
        const { data } = await api.post(`/api/lookup_table/excel`, form_data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return data.sheets.map((s: any): t.IExcelArraySheet => ({ name: s.name, data: s.data }));
    }

    static async getArrayFromCSV(file: File, delimiter: t.CSVDelimiter): Promise<t.IExcelArraySheet[]> {
        const form_data = new FormData();
        form_data.append("file", file);
        form_data.append("delimiter", delimiter);

        const { data } = await api.post(`/api/lookup_table/csv`, form_data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return data.sheets.map((s: any): t.IExcelArraySheet => ({ name: s.name, data: s.data }));
    }

    // REGIONS

    static async checkRegionRedirect(req: ICheckRegionRedirectRequest): Promise<ICheckRegionRedirectResult> {
        const { data } = await api.post(`/auth/check-region-redirect`, req);
        return data;
    }

    static async getRegions(): Promise<IRegionsResult> {
        const { data } = await api.get(`/api/regions`);
        return data;
    }

    // AUXILIARY

    static async getSourceIps(): Promise<ISourceIpsResult> {
        const { data } = await api.get(`/api/source_ips`);
        return data;
    }
}
